import { showToast } from "./stores";

/**
 * Handing a generated file to the user.
 *
 * Two paths, because the app runs in two places: inside a sandboxed host that blocks a
 * plain `<a download>` and offers its own downloads bridge instead, and in an ordinary
 * browser where an object-URL link is all there is. Every exporter needs both, so the
 * choice lives here rather than being repeated once per format.
 */

/** The host's downloads bridge, if this page is running inside one. */
interface DownloadsBridge {
  save(file: { filename: string; data: Blob | string }): Promise<unknown>;
}

async function hostDownloads(): Promise<DownloadsBridge | null> {
  const host = (window as unknown as { claude?: { use?: (name: string) => Promise<DownloadsBridge> } }).claude;
  return host?.use ? await host.use("downloads") : null;
}

/**
 * Offers `data` to the user as `filename`. `mimeType` is only used on the object-URL
 * fallback path — the bridge takes whatever it's given and decides for itself.
 */
export async function downloadFile(filename: string, data: Blob | string, mimeType: string): Promise<void> {
  const downloads = await hostDownloads();
  if (downloads) {
    try {
      await downloads.save({ filename, data });
      showToast("Export offered — check your browser's download prompt");
    } catch {
      showToast("Export was not completed");
    }
    return;
  }
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
